/**
 * ReconciliationResultTable — Phase 6a.20e per Frontend Spec §21.7.8e.
 *
 * Renders the per-contract prior year reconciliation produced by the
 * backend `priorYearReconciliation` engine (Phase 5). Each row maps
 * one ReconciliationLine to a five-column row: Hesap / Tool / Client /
 * Fark / PAJE. Material rows (|variance| > tolerance) get a
 * success/destructive subtle background tint on the Fark cell and a
 * Dr/Cr direction badge in the PAJE column derived from
 * `proposedPaje.lines`. Non-material rows render the in-tolerance hint
 * and an em-dash in the PAJE column.
 *
 * The diagnostic `TOTAL_LIABILITY_RECONCILIATION` line surfaces in a
 * separate bold totals row (no PAJE). On multi-currency contracts the
 * contract-ccy diagnostic line lives in its own informational section
 * at the bottom — backend semantics say no PAJE comes off contract-ccy
 * variances, so the PAJE column is omitted entirely for that section.
 *
 * No new color tokens — relies on `bg-emerald-50` / `bg-red-50` (and
 * dark-mode counterparts) already shipped via Tailwind defaults, same
 * palette family the Termination + Remeasurement cards use for
 * gain/loss indicators.
 */

import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { deriveFiscalYearClose } from "@/features/contract-form/utils/fiscalYearClose";
import { formatCurrency } from "@/lib/format";
import { cn } from "@/lib/utils";
import type {
  BalanceItem,
  JournalEntryLine,
  ReconciliationLine,
  ReconciliationResult,
} from "@/types/result";

/**
 * Phase 6a.20e-hotfix — format an ISO YYYY-MM-DD as Turkish calendar
 * DD.MM.YYYY for the header rows. The auditor sees the fiscal year
 * close on the `priorReportingDate` row and the PAJE date on
 * `reportingDate`; the 1-day relationship is obvious without explanatory
 * text.
 */
function formatTrDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  const [, y, m, d] = match;
  return `${d}.${m}.${y}`;
}

/**
 * MINI_FIX_CB — local type extension for the optional currency-basis
 * cross-check the backend may attach to the reconciliation payload. Wire
 * values are decimal-strings (same convention as `totalMaterialVariance`).
 * Kept local so we do not touch the shared `frontend/src/types/result.ts`
 * (not on this fix's closed file list); the prop type stays structurally
 * compatible with the existing `ReconciliationResult` (the field is
 * optional, so callers passing the unextended shape remain valid).
 */
type CurrencyBasisCheck = {
  reconstructedFunctional: string;
  enteredFunctional: string;
  divergence: string;
  isConsistent: boolean;
};
/**
 * PRIOR-RECON-CCY-BASIS — audit trail of which basis the backend actually
 * applied to the client-side figures (mirrors backend `currencyBasisApplied`
 * on `ReconciliationResult`). Always present in new payloads; optional on
 * the local type so older cached payloads (pre-fix) keep rendering.
 */
type CurrencyBasisApplied = {
  basis: "FUNCTIONAL" | "CONTRACT";
  commencementRate?: string;
  priorClosingRate?: string;
};
type ReconciliationResultWithBasisCheck = ReconciliationResult & {
  currencyBasisCheck?: CurrencyBasisCheck;
  currencyBasisApplied?: CurrencyBasisApplied;
};

export interface ReconciliationResultTableProps {
  reconciliation: ReconciliationResultWithBasisCheck;
  contractCurrency: string;
  functionalCurrency: string;
}

/**
 * Derive the auditor-facing PAJE direction string from the proposed JE's
 * lines. The line with `debit > 0` is the Dr side; the line with
 * `credit > 0` is the Cr side. Returns null when no PAJE is attached
 * (non-material line) so callers can show the em-dash placeholder.
 */
function describePaje(line: ReconciliationLine): string | null {
  const je = line.proposedPaje;
  if (!je) return null;
  const drLine = je.lines.find((l: JournalEntryLine) => Number(l.debit) > 0);
  const crLine = je.lines.find((l: JournalEntryLine) => Number(l.credit) > 0);
  if (!drLine || !crLine) return null;
  return `Dr ${drLine.accountName} / Cr ${crLine.accountName}`;
}

function isMaterialDataRow(item: BalanceItem): boolean {
  return item !== "TOTAL_LIABILITY_RECONCILIATION";
}

export function ReconciliationResultTable({
  reconciliation,
  contractCurrency,
  functionalCurrency,
}: ReconciliationResultTableProps) {
  const { t } = useTranslation();
  const isFxContract = contractCurrency !== functionalCurrency;

  // Partition lines into:
  //   - "data" rows (the 4 core balances + 3 optional extras)
  //   - "diagnostic" totals row (TOTAL_LIABILITY_RECONCILIATION at the
  //     functional level — first occurrence)
  //   - "contract-ccy" informational row (TOTAL_LIABILITY_RECONCILIATION
  //     with varianceContract set — only on FX contracts)
  const allLines = reconciliation.reconciliationLines;
  const dataLines = allLines.filter((l) => isMaterialDataRow(l.balanceItem));
  const diagnosticLines = allLines.filter(
    (l) => l.balanceItem === "TOTAL_LIABILITY_RECONCILIATION",
  );
  const functionalDiagnostic = diagnosticLines.find(
    (l) => l.varianceContract === undefined,
  );
  const contractCcyDiagnostic = diagnosticLines.find(
    (l) => l.varianceContract !== undefined,
  );

  return (
    <Card
      className="p-5 space-y-5"
      data-testid="reconciliation-result-table"
    >
      <ReconciliationHeader
        reconciliation={reconciliation}
        functionalCurrency={functionalCurrency}
      />

      {/* PRIOR-RECON-CCY-BASIS — small caption telling the auditor which
          basis was applied to the client side. Read-only audit trail; the
          per-line figures shown in the table below are always functional. */}
      {reconciliation.currencyBasisApplied && isFxContract && (
        <BasisCaption
          applied={reconciliation.currencyBasisApplied}
          contractCurrency={contractCurrency}
          functionalCurrency={functionalCurrency}
        />
      )}

      <div className="overflow-x-auto">
        <Table data-testid="reconciliation-table">
          <TableHeader>
            <TableRow>
              <TableHead>
                {t(
                  "engagement.contract.priorReconciliation.result.tableHeaders.account",
                )}
              </TableHead>
              <TableHead className="text-right">
                {t(
                  "engagement.contract.priorReconciliation.result.tableHeaders.toolComputed",
                )}
              </TableHead>
              <TableHead className="text-right">
                {t(
                  "engagement.contract.priorReconciliation.result.tableHeaders.clientReported",
                )}
              </TableHead>
              <TableHead className="text-right">
                {t(
                  "engagement.contract.priorReconciliation.result.tableHeaders.variance",
                )}
              </TableHead>
              <TableHead>
                {t(
                  "engagement.contract.priorReconciliation.result.tableHeaders.paje",
                )}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {dataLines.map((line, idx) => (
              <ReconciliationRow
                key={`${line.balanceItem}-${idx}`}
                line={line}
                currency={functionalCurrency}
              />
            ))}
            {functionalDiagnostic && (
              <DiagnosticRow
                line={functionalDiagnostic}
                currency={functionalCurrency}
              />
            )}
          </TableBody>
        </Table>
      </div>

      {reconciliation.currencyBasisCheck &&
        reconciliation.currencyBasisCheck.isConsistent === false && (
          <CurrencyBasisWarning />
        )}

      {isFxContract && contractCcyDiagnostic && (
        <ContractCcySection
          line={contractCcyDiagnostic}
          contractCurrency={contractCurrency}
        />
      )}
    </Card>
  );
}

/**
 * PRIOR-RECON-CCY-BASIS — auditor-facing caption showing which client-side
 * basis the engine applied to the reconciliation. Mirrors the form selector;
 * read-only here. Shown only for FX contracts (single-currency contracts
 * are basis-invariant by construction). For CONTRACT mode the caption also
 * cites the two rates applied per IAS 21.23 (commencement + prior-date
 * closing) so the auditor can recompute by hand if needed.
 */
interface BasisCaptionProps {
  applied: CurrencyBasisApplied;
  contractCurrency: string;
  functionalCurrency: string;
}

function BasisCaption({
  applied,
  contractCurrency,
  functionalCurrency,
}: BasisCaptionProps) {
  const { t } = useTranslation();
  if (applied.basis === "FUNCTIONAL") {
    return (
      <p
        className="text-xs italic text-muted-foreground"
        data-testid="reconciliation-basis-caption"
        data-basis="FUNCTIONAL"
      >
        {t(
          "engagement.contract.priorReconciliation.result.basisCaption.functional",
          {
            defaultValue: `Girdi bazı: Fonksiyonel (${functionalCurrency}) — çevrim yok.`,
            functionalCurrency,
          },
        )}
      </p>
    );
  }
  // CONTRACT mode — cite the two IAS 21.23 rates applied.
  return (
    <p
      className="text-xs italic text-muted-foreground"
      data-testid="reconciliation-basis-caption"
      data-basis="CONTRACT"
    >
      {t(
        "engagement.contract.priorReconciliation.result.basisCaption.contract",
        {
          defaultValue: `Girdi bazı: Sözleşme para birimi (${contractCurrency}) → IAS 21.23 ile ${functionalCurrency}'ye çevrildi. ROU brüt + birikmiş amortisman: commencement kuru (${applied.commencementRate ?? "—"} ${contractCurrency}/${functionalCurrency}). Yükümlülük ST/LT: önceki dönem kapanış kuru (${applied.priorClosingRate ?? "—"} ${contractCurrency}/${functionalCurrency}).`,
          contractCurrency,
          functionalCurrency,
          commencementRate: applied.commencementRate ?? "—",
          priorClosingRate: applied.priorClosingRate ?? "—",
        },
      )}
    </p>
  );
}

/**
 * MINI_FIX_CB — amber advisory rendered when the auditor-supplied
 * `liabilityContractCcy × priorYearClosingRate` does not reconcile to the
 * functional liability they entered. The most likely cause is that the
 * prior-year balances above were entered in the contract currency by
 * mistake — the tool expects every figure in functional currency. Read-only
 * advisory: it does NOT change any variance row, PAJE, or material flag.
 */
function CurrencyBasisWarning() {
  const { t } = useTranslation();
  return (
    <div
      data-testid="prior-reconciliation-currency-basis-warning"
      role="alert"
      className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
    >
      {t(
        "engagement.contract.priorReconciliation.result.currencyBasisWarning",
      )}
    </div>
  );
}

interface ReconciliationHeaderProps {
  reconciliation: ReconciliationResult;
  functionalCurrency: string;
}

function ReconciliationHeader({
  reconciliation,
  functionalCurrency,
}: ReconciliationHeaderProps) {
  const { t } = useTranslation();
  const unresolved = reconciliation.hasUnresolvedVariances;
  return (
    <dl
      className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm"
      data-testid="reconciliation-header"
    >
      <div className="flex justify-between gap-3">
        <dt className="text-muted-foreground">
          {t(
            "engagement.contract.priorReconciliation.result.headerLabels.priorReportingDate",
          )}
        </dt>
        {/* Phase 6a.20e-hotfix — auditor sees the calendar fiscal year
            close (engine date minus 1 day), matching the
            PriorReconciliationSection display. Falls back to the raw
            engine date if it's malformed (shouldn't happen — defensive). */}
        <dd className="font-mono">
          {formatTrDate(
            deriveFiscalYearClose(reconciliation.priorReportingDate) ??
              reconciliation.priorReportingDate,
          )}
        </dd>
      </div>
      <div className="flex justify-between gap-3">
        <dt className="text-muted-foreground">
          {t(
            "engagement.contract.priorReconciliation.result.headerLabels.reportingDate",
          )}
        </dt>
        <dd className="font-mono">
          {formatTrDate(reconciliation.reportingDate)}
        </dd>
      </div>
      <div className="flex justify-between gap-3">
        <dt className="text-muted-foreground">
          {t(
            "engagement.contract.priorReconciliation.result.headerLabels.tolerance",
          )}
        </dt>
        <dd className="font-mono">
          ±{formatCurrency(reconciliation.tolerance, functionalCurrency)}
        </dd>
      </div>
      <div className="flex justify-between gap-3">
        <dt className="text-muted-foreground">
          {t(
            "engagement.contract.priorReconciliation.result.headerLabels.totalMaterialVariance",
          )}
        </dt>
        <dd className="font-mono">
          {formatCurrency(
            reconciliation.totalMaterialVariance,
            functionalCurrency,
          )}
        </dd>
      </div>
      <div className="flex justify-between gap-3 md:col-span-2">
        <dt className="text-muted-foreground">
          {t(
            "engagement.contract.priorReconciliation.result.headerLabels.hasUnresolvedVariances",
          )}
        </dt>
        <dd>
          <Badge
            className={cn(
              "text-xs px-2 py-0.5",
              unresolved
                ? "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300",
            )}
            data-testid="reconciliation-unresolved-badge"
            data-unresolved={unresolved ? "yes" : "no"}
          >
            {t(
              unresolved
                ? "engagement.contract.priorReconciliation.result.headerValues.unresolved_yes"
                : "engagement.contract.priorReconciliation.result.headerValues.unresolved_no",
            )}
          </Badge>
        </dd>
      </div>
    </dl>
  );
}

interface ReconciliationRowProps {
  line: ReconciliationLine;
  currency: string;
}

function ReconciliationRow({ line, currency }: ReconciliationRowProps) {
  const { t } = useTranslation();
  const variance = Number(line.variance) || 0;
  const isMaterial = line.isMaterial;
  const isPositive = variance > 0;
  const isNegative = variance < 0;
  const varianceTint = isMaterial
    ? isPositive
      ? "bg-emerald-50 dark:bg-emerald-950/30"
      : isNegative
        ? "bg-red-50 dark:bg-red-950/30"
        : ""
    : "";
  const pajeDescription = describePaje(line);
  return (
    <TableRow
      data-testid={`reconciliation-row-${line.balanceItem}`}
      data-material={isMaterial ? "true" : "false"}
    >
      <TableCell className="font-medium">
        {t(
          `engagement.contract.priorReconciliation.result.balanceItems.${line.balanceItem}` as never,
        )}
      </TableCell>
      <TableCell className="text-right font-mono tabular-nums">
        {formatCurrency(line.toolComputed, currency)}
      </TableCell>
      <TableCell className="text-right font-mono tabular-nums">
        {formatCurrency(line.clientReported, currency)}
      </TableCell>
      <TableCell
        className={cn(
          "text-right font-mono tabular-nums",
          varianceTint,
        )}
        data-testid={`reconciliation-row-variance-${line.balanceItem}`}
      >
        {isMaterial ? (
          formatCurrency(line.variance, currency)
        ) : (
          <span className="text-xs text-muted-foreground italic">
            {formatCurrency("0", currency)} —{" "}
            {t(
              "engagement.contract.priorReconciliation.result.withinTolerance",
            )}
          </span>
        )}
      </TableCell>
      <TableCell data-testid={`reconciliation-row-paje-${line.balanceItem}`}>
        {pajeDescription ? (
          <span className="text-xs">{pajeDescription}</span>
        ) : (
          <span className="text-muted-foreground">
            {t("engagement.contract.priorReconciliation.result.pajeNone")}
          </span>
        )}
      </TableCell>
    </TableRow>
  );
}

interface DiagnosticRowProps {
  line: ReconciliationLine;
  currency: string;
}

function DiagnosticRow({ line, currency }: DiagnosticRowProps) {
  const { t } = useTranslation();
  return (
    <TableRow
      className="font-semibold border-t-2"
      data-testid="reconciliation-row-diagnostic"
    >
      <TableCell>
        {t(
          "engagement.contract.priorReconciliation.result.balanceItems.TOTAL_LIABILITY_RECONCILIATION",
        )}
      </TableCell>
      <TableCell className="text-right font-mono tabular-nums">
        {formatCurrency(line.toolComputed, currency)}
      </TableCell>
      <TableCell className="text-right font-mono tabular-nums">
        {formatCurrency(line.clientReported, currency)}
      </TableCell>
      <TableCell className="text-right font-mono tabular-nums">
        {formatCurrency(line.variance, currency)}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {t("engagement.contract.priorReconciliation.result.pajeNone")}
      </TableCell>
    </TableRow>
  );
}

interface ContractCcySectionProps {
  line: ReconciliationLine;
  contractCurrency: string;
}

function ContractCcySection({
  line,
  contractCurrency,
}: ContractCcySectionProps) {
  const { t } = useTranslation();
  return (
    <div
      className="rounded-md border border-muted-foreground/15 p-3 space-y-2"
      data-testid="reconciliation-contract-ccy-section"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t(
          "engagement.contract.priorReconciliation.result.contractCcySection",
        )}
      </p>
      <dl className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-1 text-sm">
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">
            {t(
              "engagement.contract.priorReconciliation.result.tableHeaders.toolComputed",
            )}
          </dt>
          <dd className="font-mono tabular-nums">
            {formatCurrency(
              line.toolComputedContract ?? line.toolComputed,
              contractCurrency,
            )}
          </dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">
            {t(
              "engagement.contract.priorReconciliation.result.tableHeaders.clientReported",
            )}
          </dt>
          <dd className="font-mono tabular-nums">
            {formatCurrency(
              line.clientReportedContract ?? line.clientReported,
              contractCurrency,
            )}
          </dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">
            {t(
              "engagement.contract.priorReconciliation.result.tableHeaders.variance",
            )}
          </dt>
          <dd className="font-mono tabular-nums">
            {formatCurrency(
              line.varianceContract ?? line.variance,
              contractCurrency,
            )}
          </dd>
        </div>
      </dl>
    </div>
  );
}
